import { faFacebook, faGithub, faSlack } from '@fortawesome/free-brands-svg-icons'
import './Header.css'
import { faCircleQuestion, faCircle as faOpenCircle } from '@fortawesome/free-regular-svg-icons'
import {
	faBars,
	faDollarSign,
	faExternalLinkSquare,
	faInfo,
	faLock,
	faStar,
	faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSubscription } from '@trpc/tanstack-react-query'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useCallback, useContext, useMemo, type HTMLAttributes, type ReactElement } from 'react'
import { PopoverActionMenu, type MenuActionItemProps, type MenuItemProps } from '~/Components/ActionMenu.js'
import { Button } from '~/Components/Button.js'
import { Grid } from '~/Components/Grid'
import { Popover } from '~/Components/Popover.js'
import { MenuSeparator } from '~/Components/useContextMenuProps.js'
import { makeAbsolutePath } from '~/Resources/util.js'
import { RootAppStoreContext } from '~/Stores/RootAppStore.js'
import { trpc } from '../Resources/TRPC.js'
import { useSidebarState } from './Sidebar.js'
import { useCompanionVersion } from './useCompanionVersion.js'

interface MyHeaderProps {
	canLock: boolean
	setLocked: (locked: boolean) => void
}

// make our own circleInfo since it's not in the free FontAwesome offering (two options here)
// note: the inline styling here is because it's aligning the compound element components rather than a stylistic choice.
function circleInfo(stacked = false): ReactElement {
	if (stacked) {
		return (
			<span className="fa-stack fa-2xs" style={{ marginLeft: '0.2em', marginRight: '-0.8em', marginTop: '-0.35em' }}>
				<FontAwesomeIcon icon={faOpenCircle} className="fa-stack-2x" style={{ marginLeft: '-0.45em' }} />
				<FontAwesomeIcon icon={faInfo} className="fa-stack-1x" style={{ marginLeft: '0.1em' }} />
			</span>
		)
	} else {
		return (
			<FontAwesomeIcon
				icon={faInfo}
				style={{
					height: '0.75em',
					width: '0.75em',
					border: '0.15em solid',
					borderRadius: '100%',
					padding: '0.15em',
					marginBottom: '-0.1em', // optional but centers slightly better vertically
				}}
			/>
		)
	}
}

export const MyHeader = observer(function MyHeader({ canLock, setLocked }: MyHeaderProps) {
	const { userConfig } = useContext(RootAppStoreContext)

	const { mobileMode, handleShowSidebar } = useSidebarState()

	const updateData = useSubscription(trpc.appInfo.updateInfo.subscriptionOptions())

	return (
		// note: position="sticky" is not necessary since the header is never part of a scrolling element.
		//  if position is sticky, the header is assigned z-index: 1020, which interferes with popups (monaco suggest-details, for example)
		//  and would likely have to be overridden anyway (to be no more than 40, in the monaco case).
		<div className="header p-0">
			<Grid.Container>
				{mobileMode && (
					<button type="button" className="header-toggler ps-1" onClick={handleShowSidebar}>
						<FontAwesomeIcon icon={faBars} />
					</button>
				)}

				<a className="header-brand mx-auto md:hidden">
					Bitfocus&nbsp;<span className="font-bold">Companion</span>
				</a>

				<HeaderNav className="hidden md:flex me-auto">
					{userConfig.properties?.installName && userConfig.properties?.installName.length > 0 && (
						<li className="nav-item install-name">{userConfig.properties?.installName}</li>
					)}

					{updateData.data?.message ? (
						<li className="nav-item header-notification-item">
							<a
								className="nav-link"
								target="_blank"
								href={updateData.data.link || 'https://companion.free/'}
								rel="noopener noreferrer"
							>
								<div className="flex">
									<div className="self-center">
										<FontAwesomeIcon icon={faTriangleExclamation} className="header-update-icon" />
									</div>
									<div>
										{updateData.data.message}
										{!!updateData.data.message2 && (
											<>
												<br />
												{updateData.data.message2}
											</>
										)}
										<FontAwesomeIcon icon={faExternalLinkSquare} className="ms-2" />
									</div>
								</div>
							</a>
						</li>
					) : (
						''
					)}
				</HeaderNav>

				<HeaderNav className="header-right">
					{canLock && (
						<Button color="primary" className="help-toggle" onClick={() => setLocked(true)} title="Lock Admin UI">
							<FontAwesomeIcon icon={faLock} className="fa-lg" />
						</Button>
					)}
				</HeaderNav>
				{/* Placing HelpMenu outside CHeaderNav gives "standard" menu line-heights. 
						Move it into the CHeaderNav block to make it look more like the sidebar line height.  */}
				<HelpMenu />
			</Grid.Container>
		</div>
	)
})

interface HeaderNavProps extends HTMLAttributes<HTMLUListElement> {
	/**
	 * A string of all className you want applied to the component.
	 */
	className?: string
}

function HeaderNav({ children, className, ...rest }: HeaderNavProps) {
	return (
		<ul className={classNames('header-nav', className)} role="navigation" {...rest}>
			{children}
		</ul>
	)
}

function HelpMenu() {
	// We could add the config wizard (showWizard) to the help menu in this useContext...
	const { whatsNewModal, notifier } = useContext(RootAppStoreContext)
	const whatsNewOpen = useCallback(() => whatsNewModal.current?.show(), [whatsNewModal])

	const { versionName, versionBuild, os, browser } = useCompanionVersion(true)
	const sysinfo = useMemo(() => {
		let version = versionName || 'version unknown'
		let versionPlus = 'Companion: ' + version
		if (versionBuild) {
			version += '\n' + versionBuild
			versionPlus += ' ' + versionBuild
		}
		versionPlus += `\nOS: ${os}\nBrowser: ${browser}\n`
		return { version, versionPlus }
	}, [versionName, versionBuild, os, browser])

	const copyVersionToClipboard = useMemo(
		// return a props object to be passed to <CopyToClipboard>
		(): MenuActionItemProps['copyToClipboard'] => ({
			text: sysinfo.versionPlus,
			onCopy: (_text, result) => {
				const success = 'Version info copied!'
				const failure = 'Failed to copy version-string to the clipboard'
				notifier.show('', result ? success : failure, 1000)
			},
		}),
		[sysinfo, notifier]
	)

	// note: the definition has to be inside a component so that we can grab `whatsNewOpen` which is a useCallback...
	const helpMenuItems: MenuItemProps[] = useMemo(
		() => [
			{
				id: 'user-guide',
				label: 'User Guide / Help',
				icon: circleInfo, // this is a function call, unlike the rest.
				href: makeAbsolutePath('/user-guide/'),
				tooltip: 'Open the User Guide in a new tab.',
				inNewTab: true,
			},
			{
				id: 'whats-new',
				label: "What's New",
				icon: faStar,
				do: whatsNewOpen,
				tooltip: 'Show the current release notes.',
				inNewTab: false,
			},
			{ ...MenuSeparator, label: 'Additional Support' },
			{
				id: 'fb',
				label: 'Community Support',
				icon: faFacebook,
				href: 'https://l.companion.free/q/6pc9ciJR5',
				tooltip: 'Share your experience or ask questions to your Companions.',
				inNewTab: true,
			},
			{
				id: 'slack',
				label: 'Slack Workspace',
				icon: faSlack,
				href: 'https://l.companion.free/q/OWxbBnDKG',
				tooltip: 'Discuss technical issues on Slack.',
				inNewTab: true,
			},
			{
				id: 'github',
				label: 'Report an Issue',
				icon: faGithub,
				href: 'https://l.companion.free/q/QZbI6mdNd',
				tooltip: 'Report bugs or request features on GitHub.',
				inNewTab: true,
			},
			MenuSeparator,
			{
				id: 'sponsor',
				label: 'Sponsor Companion',
				icon: faDollarSign,
				href: 'https://l.companion.free/q/6PtdAvZab',
				tooltip: 'Contribute funds to Bitfocus Companion.',
				inNewTab: true,
			},
			MenuSeparator,
			{
				id: 'version',
				label: sysinfo.version,
				fullWidth: true,
				do: () => {}, // no additional action needed
				tooltip: 'Click to copy version info including OS and browser to the clipboard.',
				copyToClipboard: copyVersionToClipboard,
			},
		],
		[copyVersionToClipboard, sysinfo, whatsNewOpen]
	)

	return (
		<div className="help-menu">
			<Popover.Root>
				<Popover.Trigger color="primary" className="help-toggle" aria-label="Help and support menu">
					<FontAwesomeIcon icon={faCircleQuestion} className="fa-2xl" />
				</Popover.Trigger>
				<Popover.Popup side="bottom" align="end" positionerClassName="help-menu-positioner">
					<PopoverActionMenu menuItems={helpMenuItems} />
				</Popover.Popup>
			</Popover.Root>
		</div>
	)
}
